import React, { useState, useRef, useEffect } from 'react';
import { TextField, Typography, InputAdornment } from '@mui/material';
import StyledTextField from '../styled-components/StyledTextField';
import { MAX_LENGTH_URL } from '../../constants/field-constants';
import AlertService from '../../services/notification.service';
import {
  URL_PROTOCOL_PATTERN,
  URL_STARTS_WITH_WWW_PATTERN,
  URL_LOOKS_LIKE_DOMAIN_PATTERN,
  SIMPLE_URL_VALIDATION_PATTERN,
  NUMBERS_AND_SYMBOLS_PATTERN,
  CYRILLIC_PATTERN
} from '../../constants/patterns-constants';
import {validatePlatform, type SocialPlatform} from '../helpers/validator';

type ValidationMode = "silent" | "loud";
type TextFieldProps = React.ComponentProps<typeof TextField>;

interface LinkTextFieldProps extends TextFieldProps {
  errorFields?: boolean;
  setErrorFields?: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  name: string;
  platform?: SocialPlatform | undefined;
  required?: boolean;
  check?: boolean;
  touched?: boolean;
  setTouched?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const LinkTextField = ({
                                 value,
                                 onChange,
                                 label,
                                 name,
                                 disabled,
                                 required,
                                 check,
                                 errorFields,
                                 setErrorFields,
                                 touched,
                                 setTouched,
                                 platform,
                                 ...rest
                                } :LinkTextFieldProps) => {
  const [isFocused, setIsFocused] = useState(false);
  // const [hasError, setHasError] = useState(false);
  const wasAlertedRef = useRef(false);
  const wasTrimmedRef = useRef(false);
  const wasSoftAlertedRef = useRef(false);
  const wasFocusHintShownRef = useRef(false);
  const wasCyrillicAlertRef = useRef(false);
  const wasProtocolAddedRef = useRef(false);

  useEffect(() => {
    const id = setTimeout(() => runValidation("silent"), 0);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform, value]);

  // const str = String(value ?? '');
  const isValidUrl = (input: string): boolean => {
    return SIMPLE_URL_VALIDATION_PATTERN.test(input);
  };

  const normalizeUrl = (input: string): string => {
    let trimmed = input.trim();
    if (CYRILLIC_PATTERN.test(trimmed)) {
      trimmed = trimmed.replace(CYRILLIC_PATTERN, '');
      if (!wasCyrillicAlertRef.current) {
        AlertService.warning('Cyrillic characters have been removed from the URL.');
        wasCyrillicAlertRef.current = true;
      }
    }

    if (/\s/.test(trimmed)) {
      trimmed = trimmed.replace(/\s+/g, '');
      AlertService.warning('Spaces were removed from the URL.');
    }

    if (!URL_PROTOCOL_PATTERN.test(trimmed)) {
      if ((URL_STARTS_WITH_WWW_PATTERN.test(trimmed) || URL_LOOKS_LIKE_DOMAIN_PATTERN.test(trimmed)) && !platform) {
        trimmed = 'https://' + trimmed;
        if (!wasProtocolAddedRef.current) {
          AlertService.warning('The protocol https:// was added automatically.');
          wasProtocolAddedRef.current = true;
        }
      }
    }

    if (trimmed.length > MAX_LENGTH_URL && !wasTrimmedRef.current) {
      trimmed = trimmed.slice(0, MAX_LENGTH_URL);
      AlertService.warning(`The URL was truncated to ${MAX_LENGTH_URL} characters.`);
      wasTrimmedRef.current = true;
    }

    return trimmed;
  };

  const isSoftInvalid = (val: string): boolean => {
    const text = val.trim();
    return text.length >= 6 && NUMBERS_AND_SYMBOLS_PATTERN.test(text) && !SIMPLE_URL_VALIDATION_PATTERN.test(text);
  };

  const handleFocus = () => {
    setIsFocused(true);

    wasAlertedRef.current = false;
    wasTrimmedRef.current = false;
    wasSoftAlertedRef.current = false;
    wasCyrillicAlertRef.current = false;
    wasProtocolAddedRef.current = false;

    if (!wasFocusHintShownRef.current) {
      // AlertService.warning('Please enter a valid URL like: https://example.com');
      wasFocusHintShownRef.current = true;
    }
  };

  const runValidation = (mode: ValidationMode) => {
    if (typeof value !== 'string') return;
    const normalized = normalizeUrl(value);
    const isValid = isValidUrl(normalized);
    const isEmpty = normalized.trim() === '';
    let platFormError: string | undefined;
    if (!isValid && !platform) {
      if (isEmpty && !required) {
        setErrorFields?.(prev => ({...prev, [name]: false}));
      } else {
        setErrorFields?.(prev => ({...prev, [name]: true}));
        if (!wasAlertedRef.current) {
          if (mode === "loud") {
            AlertService.warning('Please enter the correct URL, for example: https://example.com');
          }

          wasAlertedRef.current = true;
        }
      }
    } else {
        if (platform) {
          const res = validatePlatform(platform, normalized);
        if (!res.ok) {
          platFormError = res.reason || "Invalid link for the selected social network"
        }
        if (!!platFormError) {
          if (mode === "loud") {
            AlertService.warning(platFormError);
          }

          setErrorFields?.(prev => ({...prev, [name]: true}));
        } else {
          setErrorFields?.(prev => ({...prev, [name]: false}));
        }
      } else {
        setErrorFields?.(prev => ({...prev, [name]: false}));
      }
    }

    if (normalized !== value) {
      const syntheticEvent = { target: { value: normalized } } as React.ChangeEvent<HTMLInputElement>;
      onChange?.(syntheticEvent);
    }
  };

  const handleBlur = () => {
    if (typeof value !== 'string') return;
    const trimmed = value.trim();
    if (trimmed === '') {
      if (required) {
        setErrorFields?.(prev => ({...prev, [name]: true}));
        AlertService.warning(`The ${label} field must be filled in.`);
        return ;
      }
      setIsFocused(false);
      setErrorFields?.(prev => ({...prev, [name]: false}));
      return;
    }
    runValidation("loud");
    // const normalized = normalizeUrl(value);
    // const isValid = isValidUrl(normalized);
    // const isEmpty = normalized.trim() === '';
    // let platFormError: string | undefined;
    // console.log('normalized', normalized);
    // if (!isValid) {
    //   if (isEmpty && !rest.required) {
    //     setErrorFields?.(prev => ({...prev, [name]: false}));
    //   } else {
    //     setErrorFields?.(prev => ({...prev, [name]: true}));
    //     if (!wasAlertedRef.current) {
    //       AlertService.warning('Please enter the correct URL, for example: https://example.com');
    //       wasAlertedRef.current = true;
    //     }
    //   }
    // } else {
    //   console.log("platform", platform);
    //   if (platform) {
    //     console.log("platform", platform);
    //     const res = validatePlatform(platform, normalized);
    //     if (!res.ok) {
    //       platFormError = res.reason || "Invalid link for the selected social network"
    //     }
    //     if (!!platFormError) {
    //       AlertService.warning(platFormError);
    //       setErrorFields?.(prev => ({...prev, [name]: true}));
    //     } else {
    //       setErrorFields?.(prev => ({...prev, [name]: false}));
    //     }
    //   } else {
    //     setErrorFields?.(prev => ({...prev, [name]: false}));
    //   }
    // }
    //
    // if (normalized !== value) {
    //   const syntheticEvent = { target: { value: normalized } } as React.ChangeEvent<HTMLInputElement>;
    //   onChange?.(syntheticEvent);
    // }
    setIsFocused(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let currentValue = e.target.value;
    // const withProtocol = autoAddProtocol(currentValue);
    if (touched !== undefined) {
      if (!touched) setTouched?.(true);
    }

    if (isSoftInvalid(currentValue)) {
      if (!wasSoftAlertedRef.current) {
        AlertService.warning('Looks like you are entering only numbers or invalid characters. Please enter a valid URL.');
        wasSoftAlertedRef.current = true;
      }
    }
    onChange?.(e);
  };

  // const cleanInput = (raw: string): string => {
  //   return raw.replace(LATIN_TEXT_PATTERN, '').replace(/(['\-\s])\1+/g, '$1').replace(/^[\s'-]+|[\s'-]+$/g, '');
  // };
  // const cleanOnChange = (raw: string): string => {
  //   return raw.replace(LATIN_TEXT_PATTERN, '').replace(DUPLICATE_SYMBOLS_PATTERN, '$1');
  // };
  // const trimOuterEdges= (raw: string): string => {
  //   return raw.replace(OUTER_EDGES_PATTERN, '');
  // }

  // const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   let rawValue: string = e.target.value;
  //
  //   let trimmed: boolean = false;
  //   if (rawValue.length > MAX_LENGTH_FIELDS) {
  //     rawValue = rawValue.slice(0, MAX_LENGTH_FIELDS);
  //     trimmed = true;
  //   }
  //
  //   const cleaned = rawValue.replace(NON_DiGIT_PATTERN, '');
  //   const syntheticEvent = { target: { value: cleaned } } as React.ChangeEvent<HTMLInputElement>;
  //
  //   if (trimmed && !wasTrimmedRef.current) {
  //     AlertService.warning(`The text entered the ${label} field was truncated to ${MAX_LENGTH_FIELDS} characters`);
  //     wasTrimmedRef.current = true;
  //     setHasWarning(true);
  //   }
  //
  //   if (cleaned !== rawValue && !wasAlertedRef.current) {
  //     AlertService.warning(`Invalid characters have been removed from the ${label} field. Only numbers are allowed.`);
  //     wasAlertedRef.current = true;
  //     setHasWarning(true);
  //   }
  //
  //   onChange?.(syntheticEvent);
  // };
  //
  // const handleBlur = () => {
  //   setIsFocused(false);
  //   wasAlertedRef.current = false;
  //   wasTrimmedRef.current = false;
  //   const isValid = DIGITS_ONLY_PATTERN.test(value as string);
  //   setHasWarning(!isValid);

  // if (cleaned !== str) {
  //   const syntheticEvent = { target: { value: cleaned } } as React.ChangeEvent<HTMLInputElement>;
  //   AlertService.warning(`Leading and trailing spaces, apostrophes, and hyphens in the ${label} field have been removed.`);
  //   setHasWarning(true);
  //   onChange?.(syntheticEvent);
  // }
  // const hasRemainingIssues = HAS_ILLEGAL_SYMBOLS_PATTERN.test(cleaned);
  // setHasWarning(hasRemainingIssues);
  // wasAlertedRef.current = false;
  // wasTrimmedRef.current = false;
  // };

  // const handleFocus = () => {
  //   setIsFocused(true);
  //   if (!wasFocusedRef.current) {
  //     AlertService.warning(`Please enter only numbers in the ${label} field.`);
  //     wasFocusedRef.current = true;
  //   }
  // };

  return (
    <StyledTextField
      {...rest}
      label={label}
      value={value}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={handleChange}
      error={touched !== undefined ? touched && errorFields : errorFields}
      required={required || false}
      disabled={disabled || false}
      slotProps={{
        input: {
          endAdornment:
            isFocused ? (
              <InputAdornment position="end">
                <Typography
                  variant="caption"
                  color={!isFocused && errorFields ? 'error' : 'textSecondary'}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {(value as string).length}/{MAX_LENGTH_URL}
                </Typography>

              </InputAdornment>
            ) : null
        }
      }}
    />
  );
};